import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { FiCalendar, FiRefreshCw, FiInfo, FiStar } from 'react-icons/fi';

const ReservationCalendar = () => {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date(2025, 7, 1)); // August 2025
  const [carColorMap, setCarColorMap] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch vehicles
      const vehiclesResponse = await api.get('/api/cars');
      const vehiclesData = vehiclesResponse.data.data || [];

      // Clean vehicles data
      const cleanVehicles = vehiclesData.map(vehicle => ({
        _id: String(vehicle._id || vehicle.id || ''),
        brand: String(vehicle.brand || ''),
        model: String(vehicle.model || ''),
        licensePlate: String(vehicle.licensePlate || ''),
        fullName: String(vehicle.fullName || ''),
        status: String(vehicle.status || ''),
        isActive: Boolean(vehicle.isActive !== undefined ? vehicle.isActive : true)
      }));

      setVehicles(cleanVehicles);

      // Create unique color mapping for each vehicle
      const carColors = [
        'bg-purple-500',
        'bg-pink-500',
        'bg-indigo-500',
        'bg-teal-500',
        'bg-cyan-500',
        'bg-lime-500',
        'bg-amber-500',
        'bg-emerald-500',
        'bg-violet-500',
        'bg-rose-500',
        'bg-blue-600',
        'bg-green-600',
        'bg-orange-600',
        'bg-red-600',
        'bg-yellow-500',
        'bg-sky-500',
        'bg-slate-500',
        'bg-zinc-500',
        'bg-stone-500',
        'bg-neutral-500',
        'bg-fuchsia-500'
      ];

      const newCarColorMap = {};
      cleanVehicles.forEach((vehicle, index) => {
        if (index < carColors.length) {
          newCarColorMap[vehicle._id] = carColors[index];
        } else {
          // If we have more vehicles than colors, use a pattern
          newCarColorMap[vehicle._id] = carColors[index % carColors.length];
        }
      });

      setCarColorMap(newCarColorMap);

      // Fetch reservations
      const reservationsResponse = await api.get('/api/reservations');
      const reservationsData = reservationsResponse.data.data || [];

      // Clean reservations data
      const cleanReservations = reservationsData.map(reservation => ({
        _id: String(reservation._id || reservation.id || ''),
        // Keep car as-is (can be an object or string ID)
        car: reservation.car,
        // Keep client as-is (can be an object or string ID)
        client: reservation.client,
        startDate: String(reservation.startDate || ''),
        endDate: String(reservation.endDate || ''),
        status: String(reservation.status || ''),
        isActive: Boolean(reservation.isActive !== undefined ? reservation.isActive : true),
        dailyPrice: Number(reservation.dailyPrice || 0),
        totalPrice: Number(reservation.totalPrice || 0),
        paymentStatus: String(reservation.paymentStatus || ''),
        paymentMethod: String(reservation.paymentMethod || ''),
        pickupLocation: String(reservation.pickupLocation || ''),
        returnLocation: String(reservation.returnLocation || '')
      }));

      setReservations(cleanReservations);

    } catch (error) {
      console.error('❌ Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Active':
        return 'En cours';
      case 'Confirmed':
        return 'Confirmée';
      case 'Pending':
        return 'En attente';
      case 'Completed':
        return 'Terminée';
      case 'Cancelled':
        return 'Annulée';
      case 'Overdue':
        return 'En retard';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-brand-green';
      case 'Confirmed':
        return 'bg-primary-500';
      case 'Pending':
        return 'bg-yellow-500';
      case 'Completed':
        return 'bg-gray-500';
      case 'Cancelled':
        return 'bg-red-500';
      case 'Overdue':
        return 'bg-orange-500';
      default:
        return 'bg-gray-400';
    }
  };

  // Get different color for each car - NO REPETITION
  const getCarColor = (carId) => {
    // Return the assigned color from our mapping, or a default if not found
    return carColorMap[carId] || 'bg-gray-500';
  };

  // Get combined color (car color + status color)
  const getCombinedColor = (carId, status) => {
    const carColor = getCarColor(carId);
    const statusColor = getStatusColor(status);

    // Use car color as base, status color for border
    return `${carColor} border-2 ${statusColor.replace('bg-', 'border-')}`;
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
  };

  const closeReservationDetails = () => {
    setSelectedReservation(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPreviousMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + 1);
      return newDate;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Chargement du calendrier...</p>
        </div>
      </div>
    );
  }

  // Simple calendar display for now
  const renderSimpleCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate(); // Get days in current month
    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Sunday, 1 = Monday, etc.

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="border border-gray-200 p-2 min-h-[80px] bg-gray-50 dark:bg-gray-700">
        </div>
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = new Date(year, month, day);
      const dayReservations = reservations.filter(reservation => {
        const startDate = new Date(reservation.startDate);
        const endDate = new Date(reservation.endDate);
        return startDate <= currentDay && endDate >= currentDay;
      });

      days.push(
        <div key={day} className="border border-gray-200 p-2 min-h-[80px]">
          <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-1">
            {day}
            {day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear() && (
              <FiStar className="w-3 h-3 text-yellow-500" title="Aujourd'hui" />
            )}
          </div>
          {dayReservations.map((reservation, index) => {
            // Handle both string ID and object cases
            let carId = reservation.car;
            let carName = 'Voiture inconnue';

            if (typeof reservation.car === 'object' && reservation.car !== null) {
              // If car is an object, extract the ID and name
              carId = reservation.car._id || reservation.car.id || '';
              carName = reservation.car.brand && reservation.car.model
                ? `${reservation.car.brand} ${reservation.car.model}`
                : reservation.car.fullName || 'Voiture inconnue';
            } else {
              // If car is a string ID, find the vehicle
              const car = vehicles.find(v => v._id === reservation.car);
              carName = car ? `${car.brand} ${car.model}` : `Voiture ${reservation.car}`;
            }

            return (
              <div
                key={index}
                onClick={() => handleReservationClick(reservation)}
                className={`${getCombinedColor(carId, reservation.status)} text-white text-xs p-1 rounded mb-1 cursor-pointer hover:opacity-80 transition-all duration-200 hover:scale-105 ${reservation.status === 'Completed' ? 'opacity-50' : ''
                  }`}
                title={`${carName} - ${getStatusText(reservation.status)}`}
              >
                <div className="font-medium">{carName}</div>
                <div className="text-xs opacity-90">{getStatusText(reservation.status)}</div>
              </div>
            );
          })}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Calendrier de Réservation</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Vue d'ensemble des réservations et disponibilités des véhicules
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={goToToday}
            className="btn bg-primary-600 hover:bg-primary-700 text-white shadow-md flex items-center gap-2 transition-all text-sm"
          >
            <FiCalendar className="w-4 h-4" />
            <span className="hidden sm:inline">Aujourd'hui</span>
            <span className="sm:hidden">Auj.</span>
          </button>
          <button
            onClick={fetchData}
            className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all text-sm"
            title="Rafraîchir les données"
          >
            <FiRefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Rafraîchir</span>
          </button>
        </div>
      </div>

      {/* Simple Calendar Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800 dark:text-white capitalize border-l-4 border-brand-green pl-3">
            {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </h2>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-200 transition-colors text-sm"
              title="Mois précédent"
            >
              <span className="hidden sm:inline">← Précédent</span>
              <span className="sm:hidden">←</span>
            </button>

            <button
              onClick={goToToday}
              className="px-3 sm:px-4 py-2 bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              title="Aller à aujourd'hui"
            >
              <FiCalendar className="w-4 h-4" />
              <span className="hidden sm:inline">Aujourd'hui</span>
            </button>

            <button
              onClick={goToNextMonth}
              className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-200 transition-colors text-sm"
              title="Mois suivant"
            >
              <span className="hidden sm:inline">Suivant →</span>
              <span className="sm:hidden">→</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 overflow-x-auto">
          {/* Day headers */}
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Lun</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Mar</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Mer</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Jeu</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Ven</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Sam</div>
          <div className="text-center font-bold text-gray-500 dark:text-gray-400 p-1 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-t-lg text-xs sm:text-sm">Dim</div>

          {/* Calendar days */}
          {renderSimpleCalendar()}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Légende des Couleurs</h3>

        {/* Car Colors */}
        <div className="mb-4 sm:mb-6">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Couleurs des Voitures :</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
            {vehicles.slice(0, 15).map((vehicle, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-4 h-4 ${getCarColor(vehicle._id)} rounded-md flex-shrink-0`}></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {vehicle.brand} {vehicle.model}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Status Colors */}
        <div>
          <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Bordures des Statuts :</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 border-2 border-brand-green rounded-md flex-shrink-0"></div>
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">En cours</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 border-2 border-primary-500 rounded-md flex-shrink-0"></div>
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Confirmée</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 border-2 border-yellow-500 rounded-md flex-shrink-0"></div>
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">En attente</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 border-2 border-gray-500 rounded-md flex-shrink-0"></div>
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Terminée</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 border-2 border-red-500 rounded-md flex-shrink-0"></div>
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Annulée</span>
            </div>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-4">💡 Chaque voiture a sa couleur unique, les bordures indiquent le statut. Cliquez pour voir les détails.</p>
      </div>

      {/* Reservation Details Modal */}
      {selectedReservation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all scale-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Détails de la Réservation
              </h3>
              <button
                onClick={closeReservationDetails}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Statut:</span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(selectedReservation.status)}`}>
                  {getStatusText(selectedReservation.status)}
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Dates:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {new Date(selectedReservation.startDate).toLocaleDateString('fr-FR')} - {new Date(selectedReservation.endDate).toLocaleDateString('fr-FR')}
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Prix:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {selectedReservation.totalPrice} MAD
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Paiement:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {selectedReservation.paymentStatus} - {selectedReservation.paymentMethod}
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Lieu de retrait:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {selectedReservation.pickupLocation}
                </span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Lieu de retour:</span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {selectedReservation.returnLocation}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeReservationDetails}
                className="px-4 py-2 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationCalendar;
